import { useContext } from 'react';
import { store } from 'components/Store';
import { mentionsGraphNations } from './createAnalyticsState';

function useSelectedCountriesFilters(props) {
  const globalState = useContext(store);
  const { state } = globalState;
  const { analyticsState } = state ?? {};
  const { interestByRegionSection, selectedCountriesProps } = props ?? {};
  const { selectedCountries: selectedCountriesProp, selectedCountriesInterestByRegion } =
    props?.analyticsState ?? analyticsState;

  const selectedCountries = selectedCountriesProps
    ? selectedCountriesProps
    : window.location.pathname.startsWith('/analytics/dashboard')
      ? interestByRegionSection
        ? [mentionsGraphNations[0]]
        : mentionsGraphNations
      : window.location.pathname.includes('/interest-by-region') || interestByRegionSection
        ? selectedCountriesInterestByRegion
        : selectedCountriesProp;

  const getSelectedCountriesFilter = (excludeWrittenQuestions) => {
    let filters = [];
    if (selectedCountries?.find((item) => item.value === 'Scotland')) {
      filters = [
        ...filters,
        {
          field: 'contentType',
          value: 'ScotlandCountrySpecificParliamentaryContribution',
          operator: 'str_eq',
        },
        ...(excludeWrittenQuestions
          ? []
          : [
              {
                field: 'contentType',
                value: 'ScotlandQuestionAndAnswer',
                operator: 'str_eq',
              },
            ]),
      ];
    }
    if (selectedCountries?.find((item) => item.value === 'Wales')) {
      filters = [
        ...filters,
        {
          field: 'contentType',
          value: 'WalesCountrySpecificParliamentaryContribution',
          operator: 'str_eq',
        },
        ...(excludeWrittenQuestions
          ? []
          : [
              {
                field: 'contentType',
                value: 'WalesQuestionAndAnswer',
                operator: 'str_eq',
              },
            ]),
        {
          field: 'contentType',
          value: 'SeneddCommitteeTranscriptContribution',
          operator: 'str_eq',
        },
      ];
    }
    if (selectedCountries?.find((item) => item.value === 'UK')) {
      filters = [
        ...filters,
        ...(excludeWrittenQuestions
          ? []
          : [
              {
                field: 'contentType',
                value: 'QuestionAndAnswer',
                operator: 'str_eq',
              },
            ]),
        {
          field: 'contentType',
          value: 'ParliamentaryContribution',
          operator: 'str_eq',
        },
        {
          field: 'contentType',
          value: 'Edm',
          operator: 'str_eq',
        },
      ];
    }

    if (selectedCountries?.find((item) => item.value === 'Northern Ireland')) {
      filters = [
        ...filters,
        {
          field: 'contentType',
          value: 'NorthernIrelandCountrySpecificParliamentaryContribution',
          operator: 'str_eq',
        },
        ...(excludeWrittenQuestions
          ? []
          : [
              {
                field: 'contentType',
                value: 'NorthernIrelandQuestionAndAnswer',
                operator: 'str_eq',
              },
            ]),
      ];
    }

    return filters;
  };

  const countrySpecificFilterMapping = {
    Scotland: {
      ParliamentaryContribution: 'ScotlandCountrySpecificParliamentaryContribution',
      QuestionAndAnswer: 'ScotlandQuestionAndAnswer',
    },
    Wales: {
      ParliamentaryContribution: 'WalesCountrySpecificParliamentaryContribution',
      QuestionAndAnswer: 'WalesQuestionAndAnswer',
    },
    NorthernIreland: {
      ParliamentaryContribution: 'NorthernIrelandCountrySpecificParliamentaryContribution',
      QuestionAndAnswer: 'NorthernIrelandQuestionAndAnswer',
    },
  };

  const coutrySpecificFilter = (country, value) => {
    return [
      {
        field: 'contentType',
        value: countrySpecificFilterMapping[country][value],
        operator: 'str_eq',
      },
    ];
  };

  const getCountrySpecificFilters = ({ value, field }) => {
    let filters = [];

    if (value === 'ParliamentaryContribution') {
      if (UKSelected) {
        filters = [
          ...filters,
          {
            field: 'contentType',
            value: value,
            operator: 'str_eq',
          },
        ];
      }
      if (ScotlandSelected) {
        filters = [...filters, ...coutrySpecificFilter('Scotland', value)];
      }
      if (WalesSelected) {
        filters = [...filters, ...coutrySpecificFilter('Wales', value)];
      }
      if (NorthernIrelandSelected) {
        filters = [...filters, ...coutrySpecificFilter('NorthernIreland', value)];
      }
    } else if (
      (value === 'Edm' && UKSelected) ||
      (value === 'SeneddCommitteeTranscriptContribution' && WalesSelected)
    ) {
      filters = [
        ...filters,
        {
          field: field,
          value: value,
          operator: 'str_eq',
        },
      ];
    }
    return filters;
  };

  const UKSelected = selectedCountries?.find((item) => item.value === 'UK');
  const WalesSelected = selectedCountries?.find((item) => item.value === 'Wales');
  const ScotlandSelected = selectedCountries?.find((item) => item.value === 'Scotland');
  const NorthernIrelandSelected = selectedCountries?.find((item) => item.value === 'NorthernIreland');

  const parliamentaryPositionValuesByCountry = {
    UK: ['Backbencher', 'Government', 'Shadow Government', 'Shadow Cabinet', 'Cabinet'],
    Wales: ['MS'],
    Scotland: ['MSP'],
    NorthernIreland: ['MLA'],
  };

  const getParliamentaryPositionsFilters = () =>
    [
      ...(UKSelected ? parliamentaryPositionValuesByCountry.UK : []),
      ...(WalesSelected ? parliamentaryPositionValuesByCountry.Wales : []),
      ...(ScotlandSelected ? parliamentaryPositionValuesByCountry.Scotland : []),
      ...(NorthernIrelandSelected ? parliamentaryPositionValuesByCountry.NorthernIreland : []),
    ].map((value) => ({ field: 'contactPositionCategories', value, operator: 'str_eq' }));

  // WrittenQuestion is queried by 'country' rather than 'contentType' (it's its own entity type,
  // not a ParliamentaryRecord contentType). Northern Ireland written questions aren't handled
  // here yet - unlike Parliamentary position above, this hasn't been wired up for NI.
  const getWrittenQuestionCountryFilters = () => [
    ...(UKSelected ? [{ field: 'country', value: 'United Kingdom', operator: 'str_eq' }] : []),
    ...(WalesSelected ? [{ field: 'country', value: 'Wales', operator: 'str_eq' }] : []),
    ...(ScotlandSelected ? [{ field: 'country', value: 'Scotland', operator: 'str_eq' }] : []),
  ];

  return {
    getSelectedCountriesFilter,
    UKSelected,
    WalesSelected,
    ScotlandSelected,
    NorthernIrelandSelected,
    coutrySpecificFilter,
    getCountrySpecificFilters,
    getWrittenQuestionCountryFilters,
    getParliamentaryPositionsFilters,
  };
}

export default useSelectedCountriesFilters;
